import React from "react";
import {Autocomplete, Avatar, Box, Divider, TextField, Typography} from "@mui/material";
import {useSelector} from "react-redux";
import {SteamAccount} from "../../store/slices/authSlice";
import {RootState} from "../../store";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {useNavigate} from "react-router-dom";

interface SteamAccountSelectProps {
    value: SteamAccount | null | undefined;
    onChange: (steamAccount: SteamAccount | null) => void;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    label?: string;
    showAvatar?: boolean;
}

const SteamAccountSelect: React.FC<SteamAccountSelectProps> = ({
                                                                   value,
                                                                   onChange,
                                                                   size = 'medium',
                                                                   variant = 'outlined',
                                                                   label,
                                                                   showAvatar = true
                                                               }) => {
    const authAccounts = useSelector((state: RootState) => state.auth.steamAccounts);
    const navigate = useNavigate();

    const handleAddAccount = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigate('/profile/connect-account');
    };

    // Resolve value from authAccounts to ensure exact object reference matching
    const resolvedValue = authAccounts.find(acc => String(acc.id) === String(value?.id)) || value || undefined;

    return (
        <Autocomplete
            key={authAccounts.length}
            options={authAccounts}
            getOptionLabel={(option) => option.profileName || ''}
            value={resolvedValue}
            isOptionEqualToValue={(option, val) => String(option.id) === String(val?.id)}
            disableClearable
            openOnFocus
            slotProps={{
                popper: {
                    placement: 'bottom-start',
                    modifiers: [{name: 'offset', options: {offset: [0, 10]}}],
                },
                paper: {
                    className: "shadow-2xl border border-neutral-700 bg-neutral-800 !overflow-visible !rounded-lg",
                    elevation: 0,
                    sx: {
                        '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            left: 20,
                            width: 12,
                            height: 12,
                            background: 'linear-gradient(135deg, #262626 50%, transparent 50%)',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 50,
                            borderLeft: '1px solid #404040',
                            borderTop: '1px solid #404040'
                        },
                        '&:has(li:first-of-type:hover) ::before, &:has(li:first-of-type[aria-selected="true"]) ::before': {
                            background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.15) 50%, transparent 50%)',
                        }
                    }
                },
                listbox: {
                    className: "!p-0 relative z-10 !rounded-lg bg-transparent overflow-hidden",
                    sx: {
                        '& .MuiAutocomplete-option': {
                            padding: '0 !important',
                            margin: '0 !important',
                            backgroundColor: 'transparent !important',
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: 'transparent !important',
                        },
                    }
                }
            }}
            onChange={(_, newValue) => {
                if (newValue && 'id' in newValue) {
                    onChange(newValue);
                }
            }}
            size={size}
            renderOption={(props, option, state) => {
                const {key, ...optionProps} = props as any;
                const isTradeable = option.tradeable;
                const isSelected = String(resolvedValue?.id) === String(option.id);

                const isFirst = state.index === 0;
                const isLast = state.index === authAccounts.length - 1;

                return (
                    <React.Fragment key={option.id}>
                        <li {...optionProps}>
                            <Box
                                className={`flex items-center gap-3 px-1.5 py-1.5 transition-all cursor-pointer w-full ${
                                    isSelected ? 'bg-dark-primary/15' : 'hover:bg-neutral-700/80'
                                }`}
                                sx={{
                                    borderLeft: isSelected ? '3px solid #3f51b5' : '3px solid transparent',
                                    borderBottom: '1px solid rgba(64, 64, 64, 0.3)',
                                    borderTopLeftRadius: isFirst ? '8px' : '0px',
                                    borderTopRightRadius: isFirst ? '8px' : '0px',
                                }}
                            >
                                {showAvatar && (
                                    <Avatar
                                        src={option.avatarUrl || ''}
                                        className="!h-9 !w-9 border border-neutral-700/50"
                                    />
                                )}

                                <Box className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
                                    <Box
                                        className={`flex items-center justify-center text-[9px] px-1.5 h-[16px] rounded uppercase font-black tracking-tighter border shrink-0 ${
                                            isTradeable
                                                ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                                : 'bg-red-500/10 text-red-400 border-red-500/20'
                                        }`}
                                        sx={{lineHeight: 1}}
                                    >
                                        {isTradeable ? 'Tradeable' : 'Locked'}
                                    </Box>

                                    <Typography
                                        className={`text-[13px] leading-none truncate w-full ${
                                            isSelected ? '!text-white !text-sm !font-semibold' : '!text-neutral-200 !text-sm !font-semibold'
                                        }`}
                                    >
                                        {option.profileName}
                                    </Typography>
                                </Box>
                            </Box>
                        </li>

                        {isLast && (
                            <Box
                                component="li"
                                onMouseDown={handleAddAccount}
                                className="list-none"
                            >
                                <Divider className="!border-neutral-700/50"/>
                                <Box
                                    className="flex items-center gap-3 p-2 w-full hover:bg-neutral-700/80 transition-all cursor-pointer">
                                    <Avatar className="!h-9 !w-9 !bg-neutral-700 !text-neutral-400">
                                        <PersonAddIcon fontSize="small"/>
                                    </Avatar>
                                    <Typography className="!text-sm !font-semibold !text-neutral-200">
                                        Add Account
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </React.Fragment>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                        className: params.inputProps.className + " cursor-pointer !text-sm !font-semibold"
                    }}
                    className="bg-gray-400 dark:bg-neutral-700 rounded-lg"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            cursor: 'pointer',
                            '& fieldset': {borderColor: 'transparent'},
                            '&:hover fieldset': {borderColor: 'transparent'},
                            '&.Mui-focused fieldset': {
                                borderColor: 'transparent',
                                borderWidth: '0px'
                            },
                            '& input': {
                                color: '#e5e5e5',
                                padding: '4px 2px !important',
                                cursor: 'pointer',
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                MozUserSelect: 'none',
                                msUserSelect: 'none',
                                caretColor: 'transparent',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: '#a3a3a3'
                                },
                                '&::selection': {
                                    backgroundColor: 'transparent'
                                }
                            }
                        },
                        '& .MuiInputLabel-root': {color: '#737373', fontSize: '13px'},
                        '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled': {color: '#a3a3a3'}
                    }}
                />
            )}
        />
    );
};

export default SteamAccountSelect;